import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import {
  buildContactPayload,
  contactErrorMessage,
  CONTACT_SENT_MESSAGE,
  CAPTCHA_UNAVAILABLE_MESSAGE,
} from './contactForm';
import { TURNSTILE_SITE_KEY, TURNSTILE_ACTION, loadTurnstileScript } from './turnstile';
import './ContactPage.css';

export const CAPTCHA_PROMPT = 'Please complete the "Verify you are human" check first.';

const ContactPage = ({ siteKey = TURNSTILE_SITE_KEY }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [bac, setBac] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  // True once the widget is on screen. If Cloudflare's script cannot load (a
  // dealership firewall, say) the form still sends and the server decides.
  const [captchaReady, setCaptchaReady] = useState(false);
  // True when the check cannot run here; the page then offers the email address.
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const captchaContainerRef = useRef(null);
  const captchaWidgetIdRef = useRef(null);

  const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

  useEffect(() => {
    if (!siteKey || !captchaContainerRef.current) return undefined;
    let canceled = false;
    // Set when the widget says it cannot run, which it may do during render().
    let failed = false;
    // The check cannot run in this browser: stop waiting for it, let the form
    // send (the server decides) and offer the email address. Returning true
    // tells Turnstile the error has been dealt with.
    const giveUpOnCaptcha = (code) => {
      failed = true;
      if (canceled) return true;
      console.error(`Turnstile could not run (${code || 'unsupported browser'}); sending without it.`);
      setCaptchaReady(false);
      setCaptchaToken('');
      setCaptchaFailed(true);
      return true;
    };
    loadTurnstileScript()
      .then(() => {
        if (canceled || !window.turnstile || !captchaContainerRef.current) return;
        captchaWidgetIdRef.current = window.turnstile.render(captchaContainerRef.current, {
          sitekey: siteKey,
          action: TURNSTILE_ACTION,
          callback: (token) => {
            setCaptchaToken(token);
            setCaptchaFailed(false);
          },
          'expired-callback': () => setCaptchaToken(''),
          'timeout-callback': () => setCaptchaToken(''),
          'error-callback': giveUpOnCaptcha,
          'unsupported-callback': () => giveUpOnCaptcha(),
        });
        if (!failed) setCaptchaReady(true);
      })
      .catch(() => {
        if (canceled) return;
        console.error('Turnstile script failed to load; sending without it.');
        setCaptchaFailed(true);
      });
    return () => {
      canceled = true;
      if (window.turnstile && captchaWidgetIdRef.current !== null) {
        window.turnstile.remove(captchaWidgetIdRef.current);
        captchaWidgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  // A token is good for one submission, so every attempt gets a fresh one.
  const resetCaptcha = () => {
    if (window.turnstile && captchaWidgetIdRef.current !== null) {
      window.turnstile.reset(captchaWidgetIdRef.current);
    }
    setCaptchaToken('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sending) return;

    if (!isValidEmail(email)) {
      setStatus('Please enter a valid email address.');
      return;
    }

    if (captchaReady && !captchaToken) {
      setStatus(CAPTCHA_PROMPT);
      return;
    }

    // No keyword filter here: it refused ordinary requests ("update my cards;
    // thanks") and protected nothing, since the server never builds SQL from
    // what is typed.
    setSending(true);
    setStatus(null);
    try {
      await axiosInstance.post('/contact', {
        ...buildContactPayload({ name, email, phone, bac, message }),
        ...(captchaToken ? { captchaToken } : {}),
      });
      setStatus(CONTACT_SENT_MESSAGE);
      setName('');
      setEmail('');
      setMessage('');
      setBac('');
      setPhone('');
    } catch (err) {
      // What they typed stays in the form so they can fix it and resend.
      console.error('Error sending contact message:', err);
      setStatus(contactErrorMessage(err));
    } finally {
      setSending(false);
      resetCaptcha();
    }
  };

  return (
    <div className="contact-page">
      <h2>Contact Us</h2>
      <p className="intro">Have questions or need help? Send us a message and we'll respond as soon as possible.</p>
      <form className="contact-form" onSubmit={handleSubmit}>
        <label>Name *</label>
        <input type="text" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} required />
        <label>Email *</label>
        <input type="email" maxLength={255} value={email} onChange={(e) => setEmail(e.target.value)} required />
        <label>Phone *</label>
        <input type="tel" maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} required />
        <label>Dealership BAC or name</label>
        <input type="text" maxLength={120} value={bac} onChange={(e) => setBac(e.target.value)} />
        <label>Message *</label>
        <textarea maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} rows="5" required />
        {siteKey && (
          <div className="captcha-container">
            <div ref={captchaContainerRef} data-action={TURNSTILE_ACTION} />
            {captchaFailed && <p className="captcha-fallback" role="status">{CAPTCHA_UNAVAILABLE_MESSAGE}</p>}
          </div>
        )}
        <button type="submit" disabled={sending}>{sending ? 'Sending…' : 'Send Message'}</button>
      </form>
      {status && <p className="status-message">{status}</p>}
    </div>
  );
};

export default ContactPage;
